/**
 * Repair Dispatch: charger claim contracts (gitcharger-web).
 *
 * An owner or operator claims a station (or a specific charger) so fault
 * reports route to them. Login is required. Verification is a review process:
 *
 *   1. We email a one-time code (OTP) to the work email. Confirming it proves
 *      the claimant controls that inbox.
 *   2. The claim then goes to a GitCharger admin for review (admins are emailed
 *      that a claim needs review). The email-domain match against the network's
 *      registered domain is shown to the reviewer as a signal, not an
 *      auto-approval.
 *
 * POST /claim               create a claim (server emails the OTP)
 * POST /claim/otp           resend the OTP for an existing claim
 * POST /claim/otp/verify    confirm the code; may auto-verify via domain match
 */

/** The claimant's relationship to the charger. */
export enum ClaimRole {
  OWNER = 'OWNER',
  OPERATOR = 'OPERATOR',
  MANAGER = 'MANAGER',
}

/**
 * Where a claim sits in verification. Every claim is reviewed by a GitCharger
 * admin; the email-domain match is a signal shown to the reviewer, not an
 * auto-approval.
 *   AWAITING_OTP: created, code emailed, not yet confirmed.
 *   PENDING:      code confirmed; awaiting admin review.
 *   VERIFIED:     approved by a GitCharger admin.
 *   REJECTED:     denied on review.
 */
export type ClaimVerificationStatus =
  | 'AWAITING_OTP'
  | 'PENDING'
  | 'VERIFIED'
  | 'REJECTED';

/** Payload sent to POST /claim. */
export interface ChargerClaimRequest {
  stationId: number;
  /** Omit to claim the whole station; set to claim a single charger. */
  chargerId?: number;
  /** Firebase uid of the signed-in claimant. */
  claimantUserId: string;
  role: ClaimRole;
  /** Work email the OTP is sent to; its domain drives auto-verify. */
  contactEmail: string;
  contactPhone?: string;
  /** Free-text ownership evidence, used only when it goes to manual review. */
  evidenceNote?: string;
}

/** Payload sent to POST /claim/otp/verify. */
export interface ClaimOtpVerifyRequest {
  claimId: string;
  /** The code from the email. */
  code: string;
}

/** A claim as returned by the API (POST /claim, /claim/otp/verify). */
export interface ChargerClaim extends ChargerClaimRequest {
  id: string;
  verificationStatus: ClaimVerificationStatus;
  /** ISO-8601 timestamp (server audit field). */
  createdDate: string;
  /** ISO-8601 timestamp (server audit field). */
  updatedDate: string;
  /** ISO-8601 timestamp, set once the claim is reviewed (approved or rejected). */
  reviewedAt?: string;
}
